import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DocumentTableComponent } from '../../../../shared/document-table-component/document-table-component';
import { Document } from '../../../../shared/document-directive';
import { ToastService } from '../../../../services/toast-service';
import { DocumentItem, IngestionDto } from '../../../../shared/interfaces';
import { IngestionModalComponent } from '../to-process/ingestion-modal/ingestion-modal';
import { DuplicatesActionModalComponent } from '../to-process/duplicates-action-modal/duplicates-action-modal.component';
import { TaskService } from '../../../../services/task-service';

@Component({
  selector: 'app-in-error',
  standalone: true,
  imports: [CommonModule, DocumentTableComponent],
  templateUrl: './in-error.html',
  styleUrl: './in-error.scss',
})
export class InError extends Document {
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  private readonly taskService = inject(TaskService)

  // Impostazioni base
  status: string = 'error';
  task: string = 'trigger-scan';
  currentPath = signal<string>('error/');
  selectedItems = signal<DocumentItem[]>([]);

  // Stati della modale di Errore
  isModalOpen = signal<boolean>(false);
  isFetchingError = signal<boolean>(false);
  errorDetailMessage = signal<string | null>(null);

  // --- LOGICA INGESTION ---

  rootFolderName = computed(() => {
    const segments = this.currentPath().split('/').filter((s: string) => s.length > 0);
    return segments.length >= 2 ? segments[1] : null;
  });

  canRunIngestion = computed(() => {
    const path = this.currentPath();
    const segments = path.split('/').filter((s: string) => s.length > 0);
  
    if (segments.length < 2) return false;
    
    const hasDocuments = this.documents().some((doc: DocumentItem) => doc.percorso.startsWith(path) && doc.percorso !== path);
    const hasSelection = this.selectedItems().length > 0;

    return hasDocuments && hasSelection;
  });

  isProcessing = this.taskService.isProcessing;
  private readonly globalTaskState = this.taskService.taskState;

  constructor() {
    super();
    // Se un task si completa, ricarichiamo i dati
    effect(() => {
      if (this.globalTaskState()?.status === 'COMPLETED') this.loadData();
    });
  }

  onSelectionChanged(items: DocumentItem[]) {
    this.selectedItems.set(items);
  }

  openIngestionModal() {
    const index = this.rootFolderName();
    if (!index) return;
    const modalRef = this.modalService.open(IngestionModalComponent, { centered: true, windowClass: 'agentic-custom-modal' });
    modalRef.componentInstance.indexName = index;
    modalRef.result.then(res => res && this.confirmIngestion(res)).catch(() => {});
  }

  confirmIngestion(indexName: string) {
    const selectedDocs = this.selectedItems().map(doc => doc.percorso);

    const payload: IngestionDto = { 
      indexName: indexName,
      docs: selectedDocs,
      sourceType: 'error'
    };

    this.executeIngestion(payload);
  }

  private executeIngestion(payload: IngestionDto) {
    this.isLoading.set(true);

    this.documentService.ingestDocuments(this.task, payload).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        
        if (res.status === 'DUPLICATES_FOUND' && res.conflicts) {
          this.handleDuplicatesModal(payload, res);
        } else if (res.status === 'STARTED' && res.taskId) {
          this.taskService.registerNewTask(res.taskId);
          this.selectedItems.set([]); 
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  private handleDuplicatesModal(originalPayload: IngestionDto, res: any) {
    const modalRef = this.modalService.open(DuplicatesActionModalComponent, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    modalRef.componentInstance.duplicatesList = res.conflicts;
    modalRef.componentInstance.conflictType = res.conflictType; 
    
    const totalDocsSent = originalPayload.docs?.length || 0;
    modalRef.componentInstance.allDuplicates = (totalDocsSent === res.conflicts.length);

    modalRef.result.then((userChoice: 'replace' | 'keep' | 'ignore' | 'cancel') => {
      if (!userChoice || userChoice === 'cancel') return; 

      if (userChoice === 'replace') {
        this.executeIngestion({ ...originalPayload, action: 'replace' });
      } else if (userChoice === 'keep') {
        this.executeIngestion({ ...originalPayload, action: 'keep' });
      } else if (userChoice === 'ignore') {
        
        // Estraiamo solo i path di ingresso per poterli filtrare dalla lista originale
        const duplicateIncomingPaths = res.conflicts.map((c: any) => c.incomingFile);
        const filteredDocs = originalPayload.docs?.filter(doc => !duplicateIncomingPaths.includes(doc)) || [];
        
        if (filteredDocs.length === 0) {
          this.toastService.show('Tutti i file selezionati erano duplicati. Operazione ignorata.', 'info');
          this.selectedItems.set([]); 
          return;
        }
        
        this.executeIngestion({ ...originalPayload, docs: filteredDocs });
      }
    }).catch(() => {});
  }

  // --- LOGICA DETTAGLIO ERRORE ---

  openErrorDetail(document: DocumentItem) {
    if (!document.id) {
      this.errorDetailMessage.set('Impossibile recuperare il dettaglio: fileId mancante.');
      this.isModalOpen.set(true);
      return;
    }

    this.isModalOpen.set(true);
    this.isFetchingError.set(true);
    this.errorDetailMessage.set(null);

    this.documentService.getDocumentsDetail(document.id).subscribe({
      next: (response) => {
        this.errorDetailMessage.set(response.error || 'Nessun messaggio di errore specifico trovato nel database.');
        this.isFetchingError.set(false);
      },
      error: (errorResponse) => {
        console.error('Errore nel recupero dei dettagli:', errorResponse.error.message);
        this.errorDetailMessage.set('Errore nel recupero dei dettagli: ' + errorResponse.error.message);
        this.isFetchingError.set(false);
      }
    });
  }

  closeModal() {
    this.isModalOpen.set(false);
  }
}